import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { CartContext } from '../contexts/CartContext';
import { convertCentsToDollar } from '../../utils';
import config from '../../aws-exports';
import { Notification } from 'element-react';
import ResponsiveImage from '../ResponsiveImage';

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket
} = config;

const FrequentlyBoughtTogether = ({ mainProduct, complementaryProducts }) => {
  const { addProduct } = useContext(CartContext);

  const allItems = [mainProduct, ...(complementaryProducts || [])];
  
  // State for which products in the bundle are selected
  const [selectedIds, setSelectedIds] = useState(() => allItems.map((item) => item.id));

  if (!complementaryProducts || complementaryProducts.length === 0) return null;

  const toggleSelect = (id) => {
    // Prevent unselecting the main product so the bundle retains context
    if (id === mainProduct.id) return;

    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectedItems = allItems.filter((item) => selectedIds.includes(item.id));
  const totalPriceCents = selectedItems.reduce((acc, item) => acc + (item.price || 0), 0);

  const handleAddBundleToCart = () => {
    selectedItems.forEach((item) => addProduct(item));
    Notification({
      title: 'Success',
      message: `Added ${selectedItems.length} bundle item${selectedItems.length > 1 ? 's' : ''} to cart!`,
      type: 'success'
    });
  };

  const getImgUrl = (item) => {
    if (!item.file || !item.file.key) return '';
    return `https://${bucket}.s3.${region}.amazonaws.com/public/${item.file.key}`;
  };

  return (
    <BundleContainer>
      <TitleRow>
        <SectionTitle><span role="img" aria-label="lightning">⚡</span> Frequently Bought Together</SectionTitle>
        <Subtitle>Save time with this recommended bundle</Subtitle>
      </TitleRow>

      <BundleContentGrid>
        {/* Images Row with Plus Indicators */}
        <ImagesRow>
          {allItems.map((item, idx) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && <PlusSign>+</PlusSign>}
                <ProductThumbWrap $selected={isSelected}>
                  <ResponsiveImage src={getImgUrl(item)} alt={item.description} objectFit="contain" />
                </ProductThumbWrap>
              </React.Fragment>
            );
          })}
        </ImagesRow>

        {/* Selected List & Add Bundle CTA */}
        <ControlsSection>
          <SelectionList>
            {allItems.map((item) => {
              const isMain = item.id === mainProduct.id;
              const isSelected = selectedIds.includes(item.id);

              return (
                <ItemLabel key={item.id} $isMain={isMain}>
                  <CheckboxInput
                    type="checkbox"
                    checked={isSelected}
                    disabled={isMain}
                    onChange={() => toggleSelect(item.id)}
                  />
                  <ItemDescription title={item.description}>
                    {isMain ? <strong>This item: </strong> : null}
                    {item.description}
                  </ItemDescription>
                  <ItemPrice>${convertCentsToDollar(item.price)}</ItemPrice>
                </ItemLabel>
              );
            })}
          </SelectionList>

          <SummaryBox>
            <PriceTagWrap>
              <TotalLabel>Total Bundle Price:</TotalLabel>
              <TotalPrice>${convertCentsToDollar(totalPriceCents)}</TotalPrice>
            </PriceTagWrap>

            <AddBundleBtn
              disabled={selectedItems.length === 0}
              onClick={handleAddBundleToCart}
            >
              Add {selectedItems.length} Item{selectedItems.length > 1 ? 's' : ''} to Cart
            </AddBundleBtn>
          </SummaryBox>
        </ControlsSection>
      </BundleContentGrid>
    </BundleContainer>
  );
};

export default FrequentlyBoughtTogether;

/* ---------- Styles ---------- */
const BundleContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f7faf8 100%);
  border: 1px solid rgba(1, 191, 113, 0.2);
  border-radius: 20px;
  padding: 24px;
  margin: 40px 0;
  box-shadow: 0 8px 30px rgba(1, 191, 113, 0.06);
`;

const TitleRow = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 800;
  color: #1f2d3d;
  margin: 0 0 4px;

  @media screen and (min-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.88rem;
  color: #8a99a8;
  margin: 0;
`;

const BundleContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media screen and (min-width: 850px) {
    grid-template-columns: 1.1fr 1fr;
    align-items: center;
  }
`;

const ImagesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProductThumbWrap = styled.div`
  width: 90px;
  height: 90px;
  background: #fff;
  border: 2px solid ${({ $selected }) => ($selected ? '#01bf71' : 'rgba(31, 45, 61, 0.1)')};
  opacity: ${({ $selected }) => ($selected ? '1' : '0.4')};
  border-radius: 14px;
  padding: 8px;
  display: grid;
  place-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;

  @media screen and (min-width: 500px) {
    width: 110px;
    height: 110px;
  }
`;

const PlusSign = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  color: #8a99a8;
`;

const ControlsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ItemLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #24313f;
  cursor: ${({ $isMain }) => ($isMain ? 'default' : 'pointer')};
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #01bf71;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const ItemDescription = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemPrice = styled.span`
  font-weight: 700;
  color: #0a8f57;
`;

const SummaryBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;

  @media screen and (min-width: 500px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const PriceTagWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const TotalLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #8a99a8;
  text-transform: uppercase;
`;

const TotalPrice = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  color: #0a8f57;
`;

const AddBundleBtn = styled.button`
  background: #01bf71;
  color: #fff;
  border: none;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 12px 24px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(1, 191, 113, 0.3);
  transition: all 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(1, 191, 113, 0.4);
  }

  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    box-shadow: none;
    cursor: not-allowed;
  }
`;
